import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getWhatsAppUrl } from '../../../components/feature/WhatsAppButton';

export default function ResidentialHero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Toggle background based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <section className="relative min-h-[60vh] md:min-h-[85vh] overflow-hidden">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/2063d56d77349d69e524f0264c628611/11429a8bbabca3c2bd48cc699fa75d27.png"
                alt="Tadashi Consulting"
                className="h-10 lg:h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-white hover:text-[#D4AF37]'
              }`}
            >
              About
            </Link>
            <span
              className={`w-px h-4 ${
                scrolled ? 'bg-[#D4AF37]' : 'bg-white/40'
              }`}
            ></span>
            <Link
              to="/residential-renovations"
              className="text-sm font-medium text-[#D4AF37]"
            >
              Residential
            </Link>
            <span
              className={`w-px h-4 ${
                scrolled ? 'bg-[#D4AF37]' : 'bg-white/40'
              }`}
            ></span>
            <Link
              to="/commercial-construction"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-white hover:text-[#D4AF37]'
              }`}
            >
              Commercial
            </Link>
            <span
              className={`w-px h-4 ${
                scrolled ? 'bg-[#D4AF37]' : 'bg-white/40'
              }`}
            ></span>
            <a
              href="/#gallery"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-white hover:text-[#D4AF37]'
              }`}
            >
              Gallery
            </a>
            <span
              className={`w-px h-4 ${
                scrolled ? 'bg-[#D4AF37]' : 'bg-white/40'
              }`}
            ></span>
            <a
              href={getWhatsAppUrl('Hi Tadashi Consulting, I\'d like to book a residential site inspection.')}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-[#D4AF37] text-white text-sm font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-md whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Book Inspection
            </a>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <i
              className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${
                scrolled ? 'text-gray-800' : 'text-white'
              }`}
            ></i>
          </button>
        </div>

        {/* Mobile navigation */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
            <div className="px-6 py-6 space-y-4">
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                About
              </Link>
              <Link
                to="/residential-renovations"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-[#D4AF37] py-2"
              >
                Residential
              </Link>
              <Link
                to="/commercial-construction"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                Commercial
              </Link>
              <a
                href="/#gallery"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                Gallery
              </a>
              <a
                href={getWhatsAppUrl('Hi Tadashi Consulting, I\'d like to book a residential site inspection.')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-8 py-3 bg-[#D4AF37] text-white text-sm font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-md whitespace-nowrap mt-4"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Book Inspection
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ac55a83e65d5bade08a96ecaee95131b.jpg"
          alt="Luxury Residential Renovation Johannesburg"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      </div>

      {/* Hero content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-48 pb-16 md:pb-24 w-full flex flex-col items-center text-center">
        <p className="text-xs font-semibold tracking-[0.3em] text-[#D4AF37] uppercase mb-4 md:mb-6">
          Tadashi Consulting
        </p>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
          Luxury{' '}
          <span className="font-serif text-[#D4AF37]">
            Residential Renovations
          </span>
          <br className="hidden sm:block" /> in Johannesburg
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mb-6 md:mb-10 px-2">
          Transform your home with expert{' '}
          <strong>residential construction</strong> and refined finishes.
          From complete home makeovers to precision structural upgrades, we
          deliver <strong>luxury renovations</strong> that stand the test of
          time.
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
          <a
            href={getWhatsAppUrl('Hi Tadashi Consulting, I\'d like to book a free residential site inspection.')}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 md:px-10 py-3.5 md:py-4 bg-[#D4AF37] text-white text-sm md:text-base font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-lg flex items-center gap-3 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-lg md:text-xl"></i>
            Book a Free Site Inspection
            <i className="ri-arrow-right-line text-lg md:text-xl"></i>
          </a>
          <a
            href="tel:0118752075"
            className="px-8 md:px-10 py-3.5 md:py-4 border-2 border-white/60 text-white text-sm md:text-base font-semibold rounded-full hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
          >
            Call 011 875 2075
          </a>
        </div>
      </div>
    </section>
  );
}
